/**
 * "Add a blank" on a just-in-time PO (BLU-1268): a blank staff bought that the
 * batch did not plan. The Popover + Command combobox the app already uses
 * (BaseGarmentPicker, PrintSizeCodeCombobox), with the search done by the
 * server (`shouldFilter` off).
 *
 * The list is `purchaseOrders.listJitBlanks`: one page of the blanks that are
 * just in time at this location (never the org's whole catalogue), narrowed by
 * the debounced search and by the lines already on the PO, fetched only while
 * the picker is open. The server refuses anything else on Save
 * (LINE_NOT_JUST_IN_TIME) in plain words.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { JIT_BLANKS_MAX_EXCLUDED } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SectionError } from '@/components/shared/SectionError';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { trpc } from '@/lib/trpc';
import type { AddedJitLine } from '../lib/jit-lines';
import { jitRowName } from '../lib/jit-lines';

/** Matches rendered at once; typing narrows the rest. */
const MAX_OPTIONS = 50;
const SEARCH_DEBOUNCE_MS = 250;

interface JitAddBlankPickerProps {
  locationId: string | null;
  /** SKU ids already on the PO (or added): never offered twice. */
  exclude: ReadonlySet<string>;
  onAdd: (line: AddedJitLine) => void;
}

export function JitAddBlankPicker({ locationId, exclude, onAdd }: JitAddBlankPickerProps) {
  const { t } = useTranslation('orders');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);

  // The server drops these from the page and the count; a PO past the bound
  // keeps the rest out of the list below.
  const excludeSkuIds = useMemo(() => [...exclude].sort().slice(0, JIT_BLANKS_MAX_EXCLUDED), [exclude]);

  const catalog = trpc.purchaseOrders.listJitBlanks.useQuery(
    {
      locationId: locationId ?? '',
      search: search || undefined,
      limit: MAX_OPTIONS,
      excludeSkuIds: excludeSkuIds.length > 0 ? excludeSkuIds : undefined,
    },
    {
      enabled: open && Boolean(locationId),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      placeholderData: (previous) => previous,
    },
  );

  const matches = useMemo(() => {
    const shown = (catalog.data?.blanks ?? []).filter((line) => !exclude.has(line.skuId));
    return { shown, more: Math.max(0, (catalog.data?.total ?? 0) - (catalog.data?.blanks.length ?? 0)) };
  }, [catalog.data, exclude]);

  const pick = (line: AddedJitLine) => {
    onAdd(line);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full gap-1.5 text-sm sm:h-9 sm:w-auto"
          disabled={!locationId}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('jit.lines.add')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('jit.lines.addSearch')}
            aria-label={t('jit.lines.addSearch')}
          />
          <CommandList>
            {catalog.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground" role="status">
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                {t('jit.lines.addLoading')}
              </div>
            ) : catalog.error && !catalog.data ? (
              <SectionError
                variant="compact"
                className="m-2"
                title={t('jit.lines.addFailed')}
                onRetry={() => void catalog.refetch()}
                retryLabel={t('jit.retry')}
                retrying={catalog.isFetching}
              />
            ) : matches.shown.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">
                {search ? t('jit.lines.addNoMatch') : t('jit.lines.addNone')}
              </p>
            ) : (
              <CommandGroup>
                {matches.shown.map((line) => (
                  <CommandItem key={line.skuId} value={line.skuId} onSelect={() => pick(line)} className="flex-col items-start gap-0.5">
                    <span className="text-sm">{jitRowName(line)}</span>
                    {line.sku && <span className="font-mono text-[11px] text-muted-foreground">{line.sku}</span>}
                  </CommandItem>
                ))}
                {matches.more > 0 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t('jit.lines.addMore', { count: matches.more })}
                  </p>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
